// Imports
import ___CSS_LOADER_API_SOURCEMAP_IMPORT___ from "../../node_modules/css-loader/dist/runtime/sourceMaps.js";
import ___CSS_LOADER_API_IMPORT___ from "../../node_modules/css-loader/dist/runtime/api.js";
var ___CSS_LOADER_EXPORT___ = ___CSS_LOADER_API_IMPORT___(___CSS_LOADER_API_SOURCEMAP_IMPORT___);
// Module
___CSS_LOADER_EXPORT___.push([module.id, `/* AboutUs.css
   Styles for the About Us page (AboutUs.jsx). Every class is prefixed \`about-\`
   so nothing here can leak into other pages. Colours match the landing page. */

.about-page {
  --ab-blue:   #1A3C6E;
  --ab-teal:   #0B8DA6;
  --ab-accent: #00C2A8;
  --ab-light:  #faf6f0;
  --ab-white:  #FFFFFF;
  --ab-dark:   #0D1F35;
  --ab-gray:   #5b6b7d;
  --ab-border: #D6E4EC;

  /* The app-wide stylesheet sets html/body { overflow: hidden }, so this page
     scrolls inside itself (same approach as .kinetic-landing). */
  height: 100vh;
  overflow-y: auto;
  background: var(--ab-white);
  color: var(--ab-dark);
  font-family: 'Inter', 'Segoe UI', sans-serif;
  line-height: 1.6;
}

.about-page *, .about-page *::before, .about-page *::after { box-sizing: border-box; }

.about-container {
  max-width: 1140px;
  margin: 0 auto;
  padding: 0 24px;
}

/* :where() keeps these resets at low specificity so the class rules below always win. */
.about-page :where(h1, h2, h3) { margin: 0; color: var(--ab-blue); }
.about-page :where(p) { margin: 0; }

/* ---------- Buttons ---------- */
.about-btn {
  display: inline-block;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
  border: 2px solid transparent;
  transition: background-color 0.2s, color 0.2s, border-color 0.2s;
}

.about-btn--primary { background: var(--ab-blue); color: var(--ab-white); }
.about-btn--primary:hover { background: var(--ab-teal); }

.about-btn--ghost { border-color: var(--ab-blue); color: var(--ab-blue); }
.about-btn--ghost:hover { background: var(--ab-blue); color: var(--ab-white); }

.about-btn--light { background: var(--ab-white); color: var(--ab-blue); }
.about-btn--light:hover { background: var(--ab-accent); color: var(--ab-dark); }

.about-btn--outline { border-color: rgba(255, 255, 255, 0.55); color: var(--ab-white); }
.about-btn--outline:hover { border-color: var(--ab-white); background: rgba(255, 255, 255, 0.1); }

.about-btn:focus-visible,
.about-page a:focus-visible {
  outline: 3px solid var(--ab-accent);
  outline-offset: 3px;
}

/* ---------- 0. Banner (page-title image) ---------- */
.about-banner {
  position: relative;
  overflow: hidden;
  min-height: clamp(260px, 30vw, 360px);
  display: flex;
  align-items: center;
  background: #0a0d2c center / cover no-repeat;   /* BANNER_PHOTO sets the image */
  isolation: isolate;
}

.about-banner-art { position: absolute; display: block; z-index: -2; }

.about-banner-art--plant {
  inset: 0;
  width: 100%;
  height: 100%;
}

.about-banner-art--lab {
  right: max(2vw, 12px);
  bottom: 0;
  width: clamp(200px, 27vw, 380px);
  height: auto;
  z-index: -1;
  opacity: 0.92;
}

/* dark overlay so the title stays readable on any image */
.about-banner-shade {
  position: absolute;
  inset: 0;
  z-index: -1;
  background: linear-gradient(180deg, rgba(6, 9, 32, 0.55) 0%, rgba(6, 9, 32, 0.35) 55%, rgba(6, 9, 32, 0.6) 100%);
}

.about-banner-inner { width: 100%; text-align: center; padding-bottom: 8px; }

.about-banner h1 {
  color: var(--ab-white);
  font-size: clamp(2.2rem, 5.2vw, 3.6rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.1;
  text-shadow: 0 2px 18px rgba(0, 0, 0, 0.45);
}

.about-banner p {
  margin-top: 12px;
  color: rgba(255, 255, 255, 0.88);
  font-size: clamp(0.98rem, 1.6vw, 1.2rem);
  text-shadow: 0 1px 10px rgba(0, 0, 0, 0.45);
}

/* Photo banner: the sky in this photo is bright, so the overlay is darker than for the
   illustration to keep the white title readable. The plant sits low in the picture, so
   the crop is anchored near the bottom. */
.about-banner--photo {
  background-size: cover;
  background-position: center 84%;
  background-color: #1b3550;
}

.about-banner--photo .about-banner-shade {
  background: linear-gradient(180deg, rgba(8, 24, 52, 0.72) 0%, rgba(8, 24, 52, 0.55) 55%, rgba(8, 24, 52, 0.45) 100%);
}

@media (max-width: 700px) {
  /* keep the title clear of the glassware on narrow screens */
  .about-banner-art--lab { width: 170px; opacity: 0.55; right: -14px; }
}

/* ---------- 1. Hero ---------- */
.about-hero {
  background: var(--ab-light);
  padding: 80px 0 72px;
}

.about-hero-inner {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
  gap: 56px;
  align-items: center;
}

.about-hero-copy h2 {
  font-size: clamp(2rem, 4.2vw, 3.1rem);
  font-weight: 800;
  line-height: 1.12;
  letter-spacing: -0.02em;
  margin-bottom: 20px;
}

.about-hero-copy p {
  font-size: 1.1rem;
  color: var(--ab-gray);
  max-width: 52ch;
  margin-bottom: 30px;
}

.about-hero-actions { display: flex; flex-wrap: wrap; gap: 12px; }

.about-hero-art {
  background: var(--ab-white);
  border: 1px solid var(--ab-border);
  border-radius: 14px;
  padding: 20px 20px 14px;
}

.about-hero-caption {
  font-size: 0.82rem;
  color: var(--ab-gray);
  margin-top: 8px;
}

/* Kinetics curve */
.about-curve { display: block; width: 100%; height: auto; }
.about-curve-grid { stroke: var(--ab-border); stroke-width: 1; }
.about-curve-axis { stroke: #9fb3c4; stroke-width: 1.4; }

.about-curve-line {
  fill: none;
  stroke-width: 3.5;
  stroke-linecap: round;
  stroke-dasharray: 1;
  stroke-dashoffset: 0;
}
.about-curve-line--reactant { stroke: var(--ab-blue); }
.about-curve-line--product  { stroke: var(--ab-accent); }

.about-curve-dot { fill: var(--ab-white); stroke-width: 2.5; }
.about-curve-dot--reactant { stroke: var(--ab-blue); }
.about-curve-dot--product  { stroke: var(--ab-accent); }

/* The one moment of motion on the page: the two curves draw in once on load. */
@media (prefers-reduced-motion: no-preference) {
  .about-curve-line { animation: about-draw 1.6s ease-out both; }
  .about-curve-line--product { animation-delay: 0.25s; }
  .about-curve-dot { animation: about-dot 0.4s ease-out 1.6s both; }
}

@keyframes about-draw {
  from { stroke-dashoffset: 1; }
  to   { stroke-dashoffset: 0; }
}

@keyframes about-dot {
  from { opacity: 0; }
  to   { opacity: 1; }
}

/* ---------- Shared section layout ---------- */
.about-section { padding: 84px 0; background: var(--ab-white); }
.about-section--tint { background: var(--ab-light); }

.about-section-title,
.about-story h2 {
  font-size: clamp(1.6rem, 3vw, 2.1rem);
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.01em;
}

.about-section-title { margin-bottom: 36px; }

/* ---------- 2. Story ---------- */
.about-story {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);
  gap: 64px;
  align-items: start;
}

.about-story h2 { margin-bottom: 20px; }

.about-story-text p {
  font-size: 1.05rem;
  color: var(--ab-gray);
  max-width: 60ch;
  margin-bottom: 16px;
}

.about-plate {
  border-left: 4px solid var(--ab-accent);
  background: var(--ab-light);
  border-radius: 0 12px 12px 0;
  padding: 24px 26px;
}

.about-plate h3 { font-size: 1.05rem; font-weight: 700; margin-bottom: 14px; }

.about-plate ul { list-style: none; margin: 0; padding: 0; }

.about-plate li {
  position: relative;
  padding: 9px 0 9px 26px;
  color: var(--ab-dark);
  font-size: 0.98rem;
  border-top: 1px solid var(--ab-border);
}

.about-plate li:first-child { border-top: none; }

.about-plate li::before {
  content: '\\2713';
  position: absolute;
  left: 0;
  top: 9px;
  color: var(--ab-teal);
  font-weight: 700;
}

/* ---------- 3. Principles ---------- */
.about-principles {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 36px 56px;
  margin: 0;
}

.about-principle {
  border-top: 3px solid var(--ab-blue);
  padding-top: 16px;
}

.about-principle dt {
  font-size: 1.12rem;
  font-weight: 700;
  color: var(--ab-blue);
  margin-bottom: 8px;
}

.about-principle dd {
  margin: 0;
  color: var(--ab-gray);
  max-width: 54ch;
}

/* ---------- 4. Team ---------- */
.about-team {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 32px;
}

.about-member { display: flex; gap: 16px; align-items: flex-start; }

.about-avatar {
  flex: 0 0 56px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--ab-blue);
  color: var(--ab-white);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.05rem;
}

.about-member h3 { font-size: 1.05rem; font-weight: 700; }

.about-member-role {
  color: var(--ab-teal);
  font-weight: 600;
  font-size: 0.9rem;
  margin: 2px 0 8px;
}

.about-member p { color: var(--ab-gray); font-size: 0.95rem; }

/* ---------- 5. Contact band ---------- */
.about-band {
  background: var(--ab-blue);
  color: var(--ab-white);
  padding: 56px 0;
}

.about-band-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
  flex-wrap: wrap;
}

.about-band h2 { color: var(--ab-white); font-size: 1.6rem; font-weight: 700; margin-bottom: 6px; }
.about-band p { color: rgba(255, 255, 255, 0.82); }

.about-band-actions { display: flex; flex-wrap: wrap; gap: 12px; }

/* ---------- Responsive ---------- */
@media (max-width: 960px) {
  .about-hero { padding: 56px 0 52px; }
  .about-hero-inner,
  .about-story { grid-template-columns: 1fr; gap: 36px; }
  .about-section { padding: 60px 0; }
  .about-team { grid-template-columns: 1fr; gap: 28px; }
}

@media (max-width: 600px) {
  .about-container { padding: 0 18px; }
  .about-principles { grid-template-columns: 1fr; gap: 28px; }
  .about-band-actions { width: 100%; }
  .about-hero-actions .about-btn,
  .about-band-actions .about-btn { flex: 1 1 100%; text-align: center; }
}`, "",{"version":3,"sources":["webpack://./src/styles/AboutUs.css"],"names":[],"mappings":"AAAA;;+EAE+E;;AAE/E;EACE,oBAAoB;EACpB,oBAAoB;EACpB,oBAAoB;EACpB,oBAAoB;EACpB,oBAAoB;EACpB,oBAAoB;EACpB,oBAAoB;EACpB,oBAAoB;;EAEpB;iEAC+D;EAC/D,aAAa;EACb,gBAAgB;EAChB,2BAA2B;EAC3B,qBAAqB;EACrB,4CAA4C;EAC5C,gBAAgB;AAClB;;AAEA,6DAA6D,sBAAsB,EAAE;;AAErF;EACE,iBAAiB;EACjB,cAAc;EACd,eAAe;AACjB;;AAEA,wFAAwF;AACxF,iCAAiC,SAAS,EAAE,qBAAqB,EAAE;AACnE,wBAAwB,SAAS,EAAE;;AAEnC,kCAAkC;AAClC;EACE,qBAAqB;EACrB,kBAAkB;EAClB,kBAAkB;EAClB,kBAAkB;EAClB,gBAAgB;EAChB,qBAAqB;EACrB,6BAA6B;EAC7B,gEAAgE;AAClE;;AAEA,sBAAsB,0BAA0B,EAAE,sBAAsB,EAAE;AAC1E,4BAA4B,0BAA0B,EAAE;;AAExD,oBAAoB,4BAA4B,EAAE,qBAAqB,EAAE;AACzE,0BAA0B,0BAA0B,EAAE,sBAAsB,EAAE;;AAE9E,oBAAoB,2BAA2B,EAAE,qBAAqB,EAAE;AACxE,0BAA0B,4BAA4B,EAAE,qBAAqB,EAAE;;AAE/E,sBAAsB,uCAAuC,EAAE,sBAAsB,EAAE;AACvF,4BAA4B,6BAA6B,EAAE,oCAAoC,EAAE;;AAEjG;;EAEE,mCAAmC;EACnC,mBAAmB;AACrB;;AAEA,uDAAuD;AACvD;EACE,kBAAkB;EAClB,gBAAgB;EAChB,qCAAqC;EACrC,aAAa;EACb,mBAAmB;EACnB,4CAA4C,IAAI,gCAAgC;EAChF,kBAAkB;AACpB;;AAEA,oBAAoB,kBAAkB,EAAE,cAAc,EAAE,WAAW,EAAE;;AAErE;EACE,QAAQ;EACR,WAAW;EACX,YAAY;AACd;;AAEA;EACE,qBAAqB;EACrB,SAAS;EACT,gCAAgC;EAChC,YAAY;EACZ,WAAW;EACX,aAAa;AACf;;AAEA,0DAA0D;AAC1D;EACE,kBAAkB;EAClB,QAAQ;EACR,WAAW;EACX,gHAAgH;AAClH;;AAEA,sBAAsB,WAAW,EAAE,kBAAkB,EAAE,mBAAmB,EAAE;;AAE5E;EACE,sBAAsB;EACtB,uCAAuC;EACvC,gBAAgB;EAChB,uBAAuB;EACvB,gBAAgB;EAChB,2CAA2C;AAC7C;;AAEA;EACE,gBAAgB;EAChB,gCAAgC;EAChC,wCAAwC;EACxC,2CAA2C;AAC7C;;AAEA;;0CAE0C;AAC1C;EACE,sBAAsB;EACtB,+BAA+B;EAC/B,yBAAyB;AAC3B;;AAEA;EACE,oHAAoH;AACtH;;AAEA;EACE,4DAA4D;EAC5D,yBAAyB,YAAY,EAAE,aAAa,EAAE,YAAY,EAAE;AACtE;;AAEA,kCAAkC;AAClC;EACE,2BAA2B;EAC3B,oBAAoB;AACtB;;AAEA;EACE,aAAa;EACb,uDAAuD;EACvD,SAAS;EACT,mBAAmB;AACrB;;AAEA;EACE,qCAAqC;EACrC,gBAAgB;EAChB,iBAAiB;EACjB,uBAAuB;EACvB,mBAAmB;AACrB;;AAEA;EACE,iBAAiB;EACjB,qBAAqB;EACrB,eAAe;EACf,mBAAmB;AACrB;;AAEA,sBAAsB,aAAa,EAAE,eAAe,EAAE,SAAS,EAAE;;AAEjE;EACE,2BAA2B;EAC3B,kCAAkC;EAClC,mBAAmB;EACnB,uBAAuB;AACzB;;AAEA;EACE,kBAAkB;EAClB,qBAAqB;EACrB,eAAe;AACjB;;AAEA,mBAAmB;AACnB,eAAe,cAAc,EAAE,WAAW,EAAE,YAAY,EAAE;AAC1D,oBAAoB,wBAAwB,EAAE,eAAe,EAAE;AAC/D,oBAAoB,eAAe,EAAE,iBAAiB,EAAE;;AAExD;EACE,UAAU;EACV,iBAAiB;EACjB,qBAAqB;EACrB,mBAAmB;EACnB,oBAAoB;AACtB;AACA,8BAA8B,sBAAsB,EAAE;AACtD,8BAA8B,wBAAwB,EAAE;;AAExD,mBAAmB,qBAAqB,EAAE,iBAAiB,EAAE;AAC7D,6BAA6B,sBAAsB,EAAE;AACrD,6BAA6B,wBAAwB,EAAE;;AAEvD,+EAA+E;AAC/E;EACE,oBAAoB,wCAAwC,EAAE;EAC9D,6BAA6B,sBAAsB,EAAE;EACrD,mBAAmB,4CAA4C,EAAE;AACnE;;AAEA;EACE,OAAO,oBAAoB,EAAE;EAC7B,OAAO,oBAAoB,EAAE;AAC/B;;AAEA;EACE,OAAO,UAAU,EAAE;EACnB,OAAO,UAAU,EAAE;AACrB;;AAEA,gDAAgD;AAChD,iBAAiB,eAAe,EAAE,2BAA2B,EAAE;AAC/D,uBAAuB,2BAA2B,EAAE;;AAEpD;;EAEE,qCAAqC;EACrC,gBAAgB;EAChB,gBAAgB;EAChB,uBAAuB;AACzB;;AAEA,uBAAuB,mBAAmB,EAAE;;AAE5C,mCAAmC;AACnC;EACE,aAAa;EACb,0DAA0D;EAC1D,SAAS;EACT,kBAAkB;AACpB;;AAEA,kBAAkB,mBAAmB,EAAE;;AAEvC;EACE,kBAAkB;EAClB,qBAAqB;EACrB,eAAe;EACf,mBAAmB;AACrB;;AAEA;EACE,uCAAuC;EACvC,2BAA2B;EAC3B,4BAA4B;EAC5B,kBAAkB;AACpB;;AAEA,kBAAkB,kBAAkB,EAAE,gBAAgB,EAAE,mBAAmB,EAAE;;AAE7E,kBAAkB,gBAAgB,EAAE,SAAS,EAAE,UAAU,EAAE;;AAE3D;EACE,kBAAkB;EAClB,uBAAuB;EACvB,qBAAqB;EACrB,kBAAkB;EAClB,sCAAsC;AACxC;;AAEA,8BAA8B,gBAAgB,EAAE;;AAEhD;EACE,gBAAgB;EAChB,kBAAkB;EAClB,OAAO;EACP,QAAQ;EACR,qBAAqB;EACrB,gBAAgB;AAClB;;AAEA,wCAAwC;AACxC;EACE,aAAa;EACb,gDAAgD;EAChD,cAAc;EACd,SAAS;AACX;;AAEA;EACE,oCAAoC;EACpC,iBAAiB;AACnB;;AAEA;EACE,kBAAkB;EAClB,gBAAgB;EAChB,qBAAqB;EACrB,kBAAkB;AACpB;;AAEA;EACE,SAAS;EACT,qBAAqB;EACrB,eAAe;AACjB;;AAEA,kCAAkC;AAClC;EACE,gBAAgB;EAChB,SAAS;EACT,UAAU;EACV,aAAa;EACb,gDAAgD;EAChD,SAAS;AACX;;AAEA,gBAAgB,aAAa,EAAE,SAAS,EAAE,uBAAuB,EAAE;;AAEnE;EACE,cAAc;EACd,WAAW;EACX,YAAY;EACZ,kBAAkB;EAClB,0BAA0B;EAC1B,sBAAsB;EACtB,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,gBAAgB;EAChB,kBAAkB;AACpB;;AAEA,mBAAmB,kBAAkB,EAAE,gBAAgB,EAAE;;AAEzD;EACE,qBAAqB;EACrB,gBAAgB;EAChB,iBAAiB;EACjB,iBAAiB;AACnB;;AAEA,kBAAkB,qBAAqB,EAAE,kBAAkB,EAAE;;AAE7D,0CAA0C;AAC1C;EACE,0BAA0B;EAC1B,sBAAsB;EACtB,eAAe;AACjB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,8BAA8B;EAC9B,SAAS;EACT,eAAe;AACjB;;AAEA,iBAAiB,sBAAsB,EAAE,iBAAiB,EAAE,gBAAgB,EAAE,kBAAkB,EAAE;AAClG,gBAAgB,gCAAgC,EAAE;;AAElD,sBAAsB,aAAa,EAAE,eAAe,EAAE,SAAS,EAAE;;AAEjE,qCAAqC;AACrC;EACE,cAAc,oBAAoB,EAAE;EACpC;iBACe,0BAA0B,EAAE,SAAS,EAAE;EACtD,iBAAiB,eAAe,EAAE;EAClC,cAAc,0BAA0B,EAAE,SAAS,EAAE;AACvD;;AAEA;EACE,mBAAmB,eAAe,EAAE;EACpC,oBAAoB,0BAA0B,EAAE,SAAS,EAAE;EAC3D,sBAAsB,WAAW,EAAE;EACnC;mCACiC,cAAc,EAAE,kBAAkB,EAAE;AACvE","sourcesContent":["/* AboutUs.css\n   Styles for the About Us page (AboutUs.jsx). Every class is prefixed `about-`\n   so nothing here can leak into other pages. Colours match the landing page. */\n\n.about-page {\n  --ab-blue:   #1A3C6E;\n  --ab-teal:   #0B8DA6;\n  --ab-accent: #00C2A8;\n  --ab-light:  #faf6f0;\n  --ab-white:  #FFFFFF;\n  --ab-dark:   #0D1F35;\n  --ab-gray:   #5b6b7d;\n  --ab-border: #D6E4EC;\n\n  /* The app-wide stylesheet sets html/body { overflow: hidden }, so this page\n     scrolls inside itself (same approach as .kinetic-landing). */\n  height: 100vh;\n  overflow-y: auto;\n  background: var(--ab-white);\n  color: var(--ab-dark);\n  font-family: 'Inter', 'Segoe UI', sans-serif;\n  line-height: 1.6;\n}\n\n.about-page *, .about-page *::before, .about-page *::after { box-sizing: border-box; }\n\n.about-container {\n  max-width: 1140px;\n  margin: 0 auto;\n  padding: 0 24px;\n}\n\n/* :where() keeps these resets at low specificity so the class rules below always win. */\n.about-page :where(h1, h2, h3) { margin: 0; color: var(--ab-blue); }\n.about-page :where(p) { margin: 0; }\n\n/* ---------- Buttons ---------- */\n.about-btn {\n  display: inline-block;\n  padding: 12px 24px;\n  border-radius: 8px;\n  font-size: 0.95rem;\n  font-weight: 600;\n  text-decoration: none;\n  border: 2px solid transparent;\n  transition: background-color 0.2s, color 0.2s, border-color 0.2s;\n}\n\n.about-btn--primary { background: var(--ab-blue); color: var(--ab-white); }\n.about-btn--primary:hover { background: var(--ab-teal); }\n\n.about-btn--ghost { border-color: var(--ab-blue); color: var(--ab-blue); }\n.about-btn--ghost:hover { background: var(--ab-blue); color: var(--ab-white); }\n\n.about-btn--light { background: var(--ab-white); color: var(--ab-blue); }\n.about-btn--light:hover { background: var(--ab-accent); color: var(--ab-dark); }\n\n.about-btn--outline { border-color: rgba(255, 255, 255, 0.55); color: var(--ab-white); }\n.about-btn--outline:hover { border-color: var(--ab-white); background: rgba(255, 255, 255, 0.1); }\n\n.about-btn:focus-visible,\n.about-page a:focus-visible {\n  outline: 3px solid var(--ab-accent);\n  outline-offset: 3px;\n}\n\n/* ---------- 0. Banner (page-title image) ---------- */\n.about-banner {\n  position: relative;\n  overflow: hidden;\n  min-height: clamp(260px, 30vw, 360px);\n  display: flex;\n  align-items: center;\n  background: #0a0d2c center / cover no-repeat;   /* BANNER_PHOTO sets the image */\n  isolation: isolate;\n}\n\n.about-banner-art { position: absolute; display: block; z-index: -2; }\n\n.about-banner-art--plant {\n  inset: 0;\n  width: 100%;\n  height: 100%;\n}\n\n.about-banner-art--lab {\n  right: max(2vw, 12px);\n  bottom: 0;\n  width: clamp(200px, 27vw, 380px);\n  height: auto;\n  z-index: -1;\n  opacity: 0.92;\n}\n\n/* dark overlay so the title stays readable on any image */\n.about-banner-shade {\n  position: absolute;\n  inset: 0;\n  z-index: -1;\n  background: linear-gradient(180deg, rgba(6, 9, 32, 0.55) 0%, rgba(6, 9, 32, 0.35) 55%, rgba(6, 9, 32, 0.6) 100%);\n}\n\n.about-banner-inner { width: 100%; text-align: center; padding-bottom: 8px; }\n\n.about-banner h1 {\n  color: var(--ab-white);\n  font-size: clamp(2.2rem, 5.2vw, 3.6rem);\n  font-weight: 800;\n  letter-spacing: -0.02em;\n  line-height: 1.1;\n  text-shadow: 0 2px 18px rgba(0, 0, 0, 0.45);\n}\n\n.about-banner p {\n  margin-top: 12px;\n  color: rgba(255, 255, 255, 0.88);\n  font-size: clamp(0.98rem, 1.6vw, 1.2rem);\n  text-shadow: 0 1px 10px rgba(0, 0, 0, 0.45);\n}\n\n/* Photo banner: the sky in this photo is bright, so the overlay is darker than for the\n   illustration to keep the white title readable. The plant sits low in the picture, so\n   the crop is anchored near the bottom. */\n.about-banner--photo {\n  background-size: cover;\n  background-position: center 84%;\n  background-color: #1b3550;\n}\n\n.about-banner--photo .about-banner-shade {\n  background: linear-gradient(180deg, rgba(8, 24, 52, 0.72) 0%, rgba(8, 24, 52, 0.55) 55%, rgba(8, 24, 52, 0.45) 100%);\n}\n\n@media (max-width: 700px) {\n  /* keep the title clear of the glassware on narrow screens */\n  .about-banner-art--lab { width: 170px; opacity: 0.55; right: -14px; }\n}\n\n/* ---------- 1. Hero ---------- */\n.about-hero {\n  background: var(--ab-light);\n  padding: 80px 0 72px;\n}\n\n.about-hero-inner {\n  display: grid;\n  grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);\n  gap: 56px;\n  align-items: center;\n}\n\n.about-hero-copy h2 {\n  font-size: clamp(2rem, 4.2vw, 3.1rem);\n  font-weight: 800;\n  line-height: 1.12;\n  letter-spacing: -0.02em;\n  margin-bottom: 20px;\n}\n\n.about-hero-copy p {\n  font-size: 1.1rem;\n  color: var(--ab-gray);\n  max-width: 52ch;\n  margin-bottom: 30px;\n}\n\n.about-hero-actions { display: flex; flex-wrap: wrap; gap: 12px; }\n\n.about-hero-art {\n  background: var(--ab-white);\n  border: 1px solid var(--ab-border);\n  border-radius: 14px;\n  padding: 20px 20px 14px;\n}\n\n.about-hero-caption {\n  font-size: 0.82rem;\n  color: var(--ab-gray);\n  margin-top: 8px;\n}\n\n/* Kinetics curve */\n.about-curve { display: block; width: 100%; height: auto; }\n.about-curve-grid { stroke: var(--ab-border); stroke-width: 1; }\n.about-curve-axis { stroke: #9fb3c4; stroke-width: 1.4; }\n\n.about-curve-line {\n  fill: none;\n  stroke-width: 3.5;\n  stroke-linecap: round;\n  stroke-dasharray: 1;\n  stroke-dashoffset: 0;\n}\n.about-curve-line--reactant { stroke: var(--ab-blue); }\n.about-curve-line--product  { stroke: var(--ab-accent); }\n\n.about-curve-dot { fill: var(--ab-white); stroke-width: 2.5; }\n.about-curve-dot--reactant { stroke: var(--ab-blue); }\n.about-curve-dot--product  { stroke: var(--ab-accent); }\n\n/* The one moment of motion on the page: the two curves draw in once on load. */\n@media (prefers-reduced-motion: no-preference) {\n  .about-curve-line { animation: about-draw 1.6s ease-out both; }\n  .about-curve-line--product { animation-delay: 0.25s; }\n  .about-curve-dot { animation: about-dot 0.4s ease-out 1.6s both; }\n}\n\n@keyframes about-draw {\n  from { stroke-dashoffset: 1; }\n  to   { stroke-dashoffset: 0; }\n}\n\n@keyframes about-dot {\n  from { opacity: 0; }\n  to   { opacity: 1; }\n}\n\n/* ---------- Shared section layout ---------- */\n.about-section { padding: 84px 0; background: var(--ab-white); }\n.about-section--tint { background: var(--ab-light); }\n\n.about-section-title,\n.about-story h2 {\n  font-size: clamp(1.6rem, 3vw, 2.1rem);\n  font-weight: 700;\n  line-height: 1.2;\n  letter-spacing: -0.01em;\n}\n\n.about-section-title { margin-bottom: 36px; }\n\n/* ---------- 2. Story ---------- */\n.about-story {\n  display: grid;\n  grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);\n  gap: 64px;\n  align-items: start;\n}\n\n.about-story h2 { margin-bottom: 20px; }\n\n.about-story-text p {\n  font-size: 1.05rem;\n  color: var(--ab-gray);\n  max-width: 60ch;\n  margin-bottom: 16px;\n}\n\n.about-plate {\n  border-left: 4px solid var(--ab-accent);\n  background: var(--ab-light);\n  border-radius: 0 12px 12px 0;\n  padding: 24px 26px;\n}\n\n.about-plate h3 { font-size: 1.05rem; font-weight: 700; margin-bottom: 14px; }\n\n.about-plate ul { list-style: none; margin: 0; padding: 0; }\n\n.about-plate li {\n  position: relative;\n  padding: 9px 0 9px 26px;\n  color: var(--ab-dark);\n  font-size: 0.98rem;\n  border-top: 1px solid var(--ab-border);\n}\n\n.about-plate li:first-child { border-top: none; }\n\n.about-plate li::before {\n  content: '\\2713';\n  position: absolute;\n  left: 0;\n  top: 9px;\n  color: var(--ab-teal);\n  font-weight: 700;\n}\n\n/* ---------- 3. Principles ---------- */\n.about-principles {\n  display: grid;\n  grid-template-columns: repeat(2, minmax(0, 1fr));\n  gap: 36px 56px;\n  margin: 0;\n}\n\n.about-principle {\n  border-top: 3px solid var(--ab-blue);\n  padding-top: 16px;\n}\n\n.about-principle dt {\n  font-size: 1.12rem;\n  font-weight: 700;\n  color: var(--ab-blue);\n  margin-bottom: 8px;\n}\n\n.about-principle dd {\n  margin: 0;\n  color: var(--ab-gray);\n  max-width: 54ch;\n}\n\n/* ---------- 4. Team ---------- */\n.about-team {\n  list-style: none;\n  margin: 0;\n  padding: 0;\n  display: grid;\n  grid-template-columns: repeat(3, minmax(0, 1fr));\n  gap: 32px;\n}\n\n.about-member { display: flex; gap: 16px; align-items: flex-start; }\n\n.about-avatar {\n  flex: 0 0 56px;\n  width: 56px;\n  height: 56px;\n  border-radius: 50%;\n  background: var(--ab-blue);\n  color: var(--ab-white);\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  font-weight: 700;\n  font-size: 1.05rem;\n}\n\n.about-member h3 { font-size: 1.05rem; font-weight: 700; }\n\n.about-member-role {\n  color: var(--ab-teal);\n  font-weight: 600;\n  font-size: 0.9rem;\n  margin: 2px 0 8px;\n}\n\n.about-member p { color: var(--ab-gray); font-size: 0.95rem; }\n\n/* ---------- 5. Contact band ---------- */\n.about-band {\n  background: var(--ab-blue);\n  color: var(--ab-white);\n  padding: 56px 0;\n}\n\n.about-band-inner {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  gap: 32px;\n  flex-wrap: wrap;\n}\n\n.about-band h2 { color: var(--ab-white); font-size: 1.6rem; font-weight: 700; margin-bottom: 6px; }\n.about-band p { color: rgba(255, 255, 255, 0.82); }\n\n.about-band-actions { display: flex; flex-wrap: wrap; gap: 12px; }\n\n/* ---------- Responsive ---------- */\n@media (max-width: 960px) {\n  .about-hero { padding: 56px 0 52px; }\n  .about-hero-inner,\n  .about-story { grid-template-columns: 1fr; gap: 36px; }\n  .about-section { padding: 60px 0; }\n  .about-team { grid-template-columns: 1fr; gap: 28px; }\n}\n\n@media (max-width: 600px) {\n  .about-container { padding: 0 18px; }\n  .about-principles { grid-template-columns: 1fr; gap: 28px; }\n  .about-band-actions { width: 100%; }\n  .about-hero-actions .about-btn,\n  .about-band-actions .about-btn { flex: 1 1 100%; text-align: center; }\n}"],"sourceRoot":""}]);
// Exports
___CSS_LOADER_EXPORT___.locals = {};
export default ___CSS_LOADER_EXPORT___;
